import { Employee } from './employee.model';
import { Equipment } from './equipment.model';
import { MaintenanceChecklist } from './maintenance-checklist.model';

export enum MaintenanceStatus {
  Planned = 'Planned',
  InProgress = 'InProgress',
  Completed = 'Completed',
  Overdue = 'Overdue',
  Cancelled = 'Cancelled'
}

export enum MaintenanceRecurrenceType {
  None = 'None',
  Daily = 'Daily',
  Weekly = 'Weekly',
  Monthly = 'Monthly',
  Quarterly = 'Quarterly',
  Yearly = 'Yearly',
  Custom = 'Custom'
}

export enum MaintenanceBillingStatus {
  Open = 'Open',
  Billed = 'Billed'
}

export enum MaintenanceBillingLineSourceType {
  Manual = 'Manual',
  Material = 'Material',
  Regie = 'Regie'
}

export interface User {
  id: string;
  userName: string;
  email: string;
}

export interface ProblemEntry {
  description: string;
  solution: string;
}

export interface MaintenanceMaterial {
  id: number;
  name: string;
  description?: string;
  quantity: number;
  unit: string;
  unitPrice?: number;
  isPacked: boolean;
}

export interface MaintenanceRegieLine {
  id?: number;
  customerHourlyRateId?: number | null;
  label: string;
  hourlyRate: number;
  hours: number;
  totalAmount?: number;
  isCustomerAcknowledged?: boolean;
  acknowledgedAtUtc?: string | Date | null;
}

export interface MaintenanceBillingLine {
  id?: number | null;
  sourceType: MaintenanceBillingLineSourceType;
  sourceId?: number | null;
  description: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  isIncluded: boolean;
  sortOrder: number;
  totalAmount?: number;
}

export interface MaintenanceBilling {
  maintenanceId: number;
  billingStatus: MaintenanceBillingStatus;
  billedAtUtc?: string | Date | null;
  billingNote?: string | null;
  lines: MaintenanceBillingLine[];
}

export interface MaintenanceBillingOverview {
  maintenanceId: number;
  title: string;
  scheduledDate: string | Date;
  completedOn?: string | Date | null;
  status: MaintenanceStatus;
  recurrenceType: MaintenanceRecurrenceType;
  billingStatus: MaintenanceBillingStatus;
  billedAtUtc?: string | Date | null;
  customerName: string;
  customerId?: number | null;
  equipmentName: string;
  netTotal: number;
  includedTotal: number;
  lineCount: number;
  unpricedLineCount: number;
}

export interface MaintenanceProgressUpdate {
  id: number;
  timestamp: string;
  progressPercent: number;
  notes: string;
  user?: User;
}

export interface Maintenance {
  id: number;
  equipmentId: number;
  equipment?: Equipment;
  title: string;
  scheduledDate: string | Date;
  dueDate?: string | Date;
  status: MaintenanceStatus;
  progressPercent: number;
  startedOn?: string | Date;
  completedOn?: string | Date;
  recurrenceType: MaintenanceRecurrenceType;
  recurrenceIntervalDays?: number;
  notificationsEnabled: boolean;
  notificationLeadTimeDays: number;
  nextScheduledDate?: string | Date;
  lastNotificationSentAt?: string | Date;
  assignedEmployeeId?: number | null;
  assignedEmployee?: Employee | null;
  assignedUserId?: string | null;
  assignedUser?: User | null;
  assignedUserIds?: string[];
  assignedUsers?: User[];
  notes?: string;
  durationHours?: number | null;
  durationMinutes?: number | null;
  travelHours?: number | null;
  travelMinutes?: number | null;
  arrivalTime?: string | null;
  departureTime?: string | null;
  departureFromOffice?: string | null;
  returnToOffice?: string | null;
  initialSituation?: string;
  problems?: ProblemEntry[];
  workItems?: string[];
  regieHours?: number | null;
  regieMinutes?: number | null;
  customerAcknowledged?: boolean;
  techSigneeName?: string | null;
  techSignatureDataUrl?: string | null;
  customerSigneeName?: string | null;
  customerSignatureDataUrl?: string | null;
  customerSignatureEnabled?: boolean;
  isCommerciallyRelevant?: boolean;
  billingStatus?: MaintenanceBillingStatus;
  billedAtUtc?: string | Date | null;
  billingNote?: string | null;
  checklists?: MaintenanceChecklist[];
  materials: MaintenanceMaterial[];
  regieLines?: MaintenanceRegieLine[];
  billingLines?: MaintenanceBillingLine[];
  progressUpdates: MaintenanceProgressUpdate[];
}
